<template>
  <div
    v-if="isAdmin"
    class="gp-admin-bar bg-gp-midnight-2 fixed bottom-0 flex w-full items-center justify-between p-2.5 shadow-[0_0_10px_rgba(0,0,0,0.3)]"
    :class="interactivityLockVisibility ? 'z-31' : 'z-11'"
  >
    <div class="flex gap-2">
      <gp-btn-link
        :href="adminPanelUrl"
        :label="t('ngp/admin_bar/dashboard_link')"
        target="_blank"
        :icon="faExternalLinkAlt"
        size="sm"
      />
      <owner :in-popup="false" class="border-gp-mouse/50 border-l pl-2" />
      <maintenance-lock-notification :in-popup="false" class="border-gp-mouse/50 border-l pl-2" />
    </div>
    <div class="hidden gap-2 xl:flex">
      <maintenance-lock-actions :in-popup="false" />
      <gp-btn
        size="sm"
        :label="i18nDisabled ? 'i18n: OFF' : 'i18n: ON'"
        :icon="faLanguage"
        @click="toggleI18nDisabled"
      />
      <language-select />
      <template v-if="serverInfoAvailable">
        <gp-btn
          size="sm"
          :label="t('ngp/admin_bar/server_details_button')"
          :icon="infoPopupOpen ? faChevronCircleDown : faInfoCircle"
          @click="infoPopupOpen = !infoPopupOpen"
        />
      </template>
    </div>
    <div v-if="mobilePopupAvailable" class="xl:hidden">
      <font-awesome-icon
        :icon="mobilePopupOpen ? faChevronCircleDown : faChevronCircleUp"
        @click="mobilePopupOpen = !mobilePopupOpen"
      />
    </div>
    <info-popup v-if="serverInfoAvailable" v-show="infoPopupOpen" class="hidden xl:block">
      <server-details />
    </info-popup>
    <info-popup v-if="mobilePopupAvailable" v-show="mobilePopupOpen" class="xl:hidden">
      <div class="flex flex-col gap-2">
        <owner :in-popup="true" class="border-gp-mouse/50 border-b pb-2" />
        <maintenance-lock-notification :in-popup="true" class="border-gp-mouse/50 border-b pb-2" />
        <server-details class="border-gp-mouse/50 border-b pb-2" />
        <maintenance-lock-actions :in-popup="true" />
        <div class="flex gap-2">
          <gp-btn
            size="sm"
            :label="i18nDisabled ? 'i18n: OFF' : 'i18n: ON'"
            :icon="faLanguage"
            @click="toggleI18nDisabled"
          />
          <language-select />
        </div>
      </div>
    </info-popup>
  </div>
</template>

<script setup lang="ts">
import { useClientStore } from "@/store/client";

import {
  faChevronCircleDown,
  faChevronCircleUp,
  faExternalLinkAlt,
  faInfoCircle,
  faLanguage,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { GpBtn, GpBtnLink } from "@gportal/uikit";
import { computed, ref } from "vue";

import { useI18n } from "vue-i18n";
import { i18nDisabled, toggleI18nDisabled } from "../../i18n";
import { useServiceStore } from "../../store/service";
import { usePermissions } from "../../util/permissions";
import { useWiUtils } from "../../util/webinterface";
import InfoPopup from "./InfoPopup.vue";
import LanguageSelect from "./LanguageSelect.vue";

import MaintenanceLockActions from "./MaintenanceLockActions.vue";
import MaintenanceLockNotification from "./MaintenanceLockNotification.vue";
import Owner from "./Owner.vue";
import ServerDetails from "./ServerDetails.vue";

const { t } = useI18n();
const service = useServiceStore();
const client = useClientStore();

const { adminPanelUrl } = useWiUtils();

const { isAdmin, permissionsOwner } = usePermissions();

const sid = computed(() => service.ctx.service?.config?.rsid.id);
const serverId = computed(() => service.ctx.sys?.gameServer?.id);
const hwId = computed(() => service.ctx.service?.config?.hwId);
const interactivityLockVisibility = computed(() => client.interactivityLockVisibility);

const serverInfoAvailable = computed(() => sid.value || serverId.value || hwId.value);
const mobilePopupAvailable = computed(() => !!permissionsOwner.value || serverInfoAvailable.value);

const infoPopupOpen = ref(false);
const mobilePopupOpen = ref(false);
</script>
